import { ActionContext, ActionTree } from 'vuex'
import { StoreInterface } from '../index'
import { LocalStateInterface } from './state'
import { DefaultRole, AssetUrl } from 'able-lib'
import { QueryOptions, SubscriptionOptions } from 'apollo-client'
import { apolloProvider } from 'boot/apollo'
import {
  downloadFile,
  getSecuredAssetUrl,
  removeToDownloadAssetList
} from 'src/lib/helper'
import {
  SUBSCRIPTION_UNREAD_GENERAL_ANNOUNCEMENT_COUNT,
  SUBSCRIPTION_UNREAD_PARENT_ANNOUNCEMENT_COUNT
} from 'src/lib/apollo/announcements'
import { SUBSCRIPTION_UNREAD_MESSAGE_COUNT } from 'src/lib/apollo/message'
import {
  SUBSCRIPTION_ANSWERED_ASSIGNMENT_COUNT,
  SUBSCRIPTION_TOTAL_ASSIGNMENT_COUNT,
  SUBSCRIPTION_UNANSWERED_ASSIGNMENT_COUNT
} from 'src/lib/apollo/assignments'
import { QUERY_CHECK_MAINTENANCE } from 'src/lib/apollo/common/maintenance'
import { SUBSCRIPTION_UNREAD_JOURNAL_COUNT } from 'src/lib/apollo/journals/journals'
import {
  QUERY_GET_CURRENT_YEAR,
  QUERY_GET_LEVELS,
  QUERY_GET_SUBJECTS,
  QUERY_GET_LOCATIONS,
  QUERY_GET_ROLES
} from 'src/lib/queries/local'
import { EventBus } from 'src/lib/eventBus'
import moment from 'moment'

const actions: ActionTree<LocalStateInterface, StoreInterface> = {
  async downloadAssets ({ commit, state }: ActionContext<LocalStateInterface, StoreInterface>): Promise<void> {
    const assetUrls = state.downloadAssets.assetUrls
    const urls = assetUrls.map(item => item.url)
    let download = urls.concat.apply([], urls)
    let currentDownloadingAsset: Array<AssetUrl> = []
    let attempt = 0

    download = download.filter(url => !state.downloadAssets.downloaded.includes(url))
    download = download.filter(url => !state.downloadAssets.current.includes(url))

    if (state.downloadAssets.current.length) currentDownloadingAsset = state.downloadAssets.current.concat(download)
    else currentDownloadingAsset = download
    commit('updateCurrentDownloadingAsset', currentDownloadingAsset)

    if (download.length) {
      for await (const [urlIndex, url] of download.entries()) {
        console.debug('downloadAssets.start: ' + url)
        EventBus.$on('downloadProgress', ({ url, progress }) => {
          commit('updateDownloadAssetProgress', { url, progress, error: false, attempt })
        })
        EventBus.$on('networkOffline', () => {
          attempt += 1
          commit('updateDownloadAssetProgress', { error: true, attempt })
          if (attempt <= 5) {
            setTimeout(async () => {
              const downloaded = await downloadFile(getSecuredAssetUrl(url))
              if (downloaded) {
                console.debug('downloadAssets.done: ' + url)
                attempt = 0
                commit('incrementCompletedDownloadedAsset', url)
                commit('updateCurrentDownloadingAsset', removeToDownloadAssetList(state.downloadAssets.current, url))
              } else {
                commit('updateDownloadAssetProgress', { error: true })
                commit('updateCurrentDownloadingAsset', removeToDownloadAssetList(state.downloadAssets.current, url))
              }
            }, attempt * 3000)
          } else {
            commit('updateDownloadAssetProgress', { error: true, attempt })
            commit('updateCurrentDownloadingAsset', removeToDownloadAssetList(state.downloadAssets.current, url))
          }
        })

        const downloaded = await downloadFile(getSecuredAssetUrl(url))
        if (downloaded) {
          console.debug('downloadAssets.done: ' + url)
          attempt = 0
          commit('incrementCompletedDownloadedAsset', url)
          commit('updateCurrentDownloadingAsset', removeToDownloadAssetList(state.downloadAssets.current, url))
        } else commit('updateDownloadAssetProgress', { error: true })

        if (urlIndex === assetUrls.length - 1) {
          console.debug('downloadAssets.complete')
          EventBus.$off('downloadProgress')
          EventBus.$off('networkOffline')
        }
      }
    }
  },

  async checkMaintenance ({ commit }: ActionContext<LocalStateInterface, StoreInterface>) {
    const options: SubscriptionOptions = {
      query: QUERY_CHECK_MAINTENANCE
    }

    const { data } = await apolloProvider.defaultClient.query(options)
    commit('SET_MAINTENANCE_INFO', data.maintenance[0])
  },

  updateMaintenance ({ commit }: ActionContext<LocalStateInterface, StoreInterface>, maintenanceInfo) {
    commit('SET_MAINTENANCE_INFO', maintenanceInfo)
  },

  getNotifications ({ dispatch, rootGetters }: ActionContext<LocalStateInterface, StoreInterface>, userId) {
    dispatch('listenAnnouncementNotification', userId)
    dispatch('listenChatNotification', userId)
    const userRole = rootGetters['user/getUserRole']
    if (userRole === DefaultRole.STUDENT) {
      dispatch('listenAssignmentNotification', userId)
    }
    if (userRole === DefaultRole.TEACHER) {
      dispatch('listenJournalNotification', userId)
    }
  },

  listenAnnouncementNotification ({ commit }: ActionContext<LocalStateInterface, StoreInterface>, userId) {
    const options1: SubscriptionOptions = {
      query: SUBSCRIPTION_UNREAD_GENERAL_ANNOUNCEMENT_COUNT,
      variables: {
        userId,
        data: {
          forParents: true
        }
      }
    }
    const options2: SubscriptionOptions = {
      query: SUBSCRIPTION_UNREAD_PARENT_ANNOUNCEMENT_COUNT,
      variables: {
        userId,
        data: {
          forParents: true
        }
      }
    }

    const observableQueryGeneral = apolloProvider.defaultClient.subscribe(options1)
    observableQueryGeneral.subscribe(({ data }) => {
      commit('SET_MENU_PROPS', {
        path: [5, 'children', 1, 'badge'],
        value: data.announcements_aggregate.aggregate.count
      })
    })

    const observableQueryParent = apolloProvider.defaultClient.subscribe(options2)
    observableQueryParent.subscribe(({ data }) => {
      commit('SET_MENU_PROPS', {
        path: [5, 'children', 2, 'badge'],
        value: data.announcements_aggregate.aggregate.count
      })
    })
  },

  listenChatNotification ({ commit }: ActionContext<LocalStateInterface, StoreInterface>, userId) {
    const options: SubscriptionOptions = {
      query: SUBSCRIPTION_UNREAD_MESSAGE_COUNT,
      variables: {
        userId
      }
    }

    const observableQuery = apolloProvider.defaultClient.subscribe(options)
    observableQuery.subscribe(({ data }) => {
      let count = 0
      data.unreadMessages.forEach(message => {
        count += message.count
      })
      commit('SET_MENU_PROPS', {
        path: [5, 'children', 0, 'badge'],
        value: count
      })
    })
  },

  listenAssignmentNotification ({ commit }: ActionContext<LocalStateInterface, StoreInterface>, userId) {
    // Total Count
    apolloProvider.defaultClient.subscribe({
      query: SUBSCRIPTION_TOTAL_ASSIGNMENT_COUNT,
      variables: { userId }
    }).subscribe(({ data }) => {
      const total = data.assignment_aggregate.aggregate.count
      commit('SET_TOTAL_ASSIGNMENT_COUNT', total)
    })

    // Unanswered Count
    apolloProvider.defaultClient.subscribe({
      query: SUBSCRIPTION_UNANSWERED_ASSIGNMENT_COUNT,
      variables: { userId }
    }).subscribe(({ data }) => {
      const unansweredCount = data.assignment_aggregate.aggregate.count
      commit('SET_UNANSWERED_ASSIGNMENT_COUNT', unansweredCount)
      commit('SET_MENU_PROPS', {
        path: [4, 'children', 0, 'badge'],
        value: unansweredCount
      })
    })

    // Answered Count
    apolloProvider.defaultClient.subscribe({
      query: SUBSCRIPTION_ANSWERED_ASSIGNMENT_COUNT,
      variables: { userId }
    }).subscribe(({ data }) => {
      const answeredCount = data.assignment_aggregate.aggregate.count
      commit('SET_ANSWERED_ASSIGNMENT_COUNT', answeredCount)
    })
  },

  listenJournalNotification ({ commit }: ActionContext<LocalStateInterface, StoreInterface>, userId) {
    apolloProvider.defaultClient.subscribe({
      query: SUBSCRIPTION_UNREAD_JOURNAL_COUNT,
      variables: { userId }
    }).subscribe(({ data }) => {
      commit('SET_MENU_PROPS', {
        path: [4, 'children', 1, 'badge'],
        value: data.user_content_aggregate.aggregate.count
      })
    })
  },

  async getCurrentYear ({ commit }: ActionContext<LocalStateInterface, StoreInterface>) {
    const options: QueryOptions = {
      query: QUERY_GET_CURRENT_YEAR,
      variables: {
        currentDate: moment().format('YYYY-MM-DD')
      }
    }

    const { data } = await apolloProvider.defaultClient.query(options)
    const currentYear = data.currentYear?.[0]?.year || moment().format('YYYY')
    commit('SET_CURRENT_YEAR', currentYear)
  },

  async fetchRoles ({ commit }: ActionContext<LocalStateInterface, StoreInterface>) {
    const options: QueryOptions = {
      query: QUERY_GET_ROLES
    }

    const { data } = await apolloProvider.defaultClient.query(options)
    commit('SET_ROLES', data.role)
  },

  async fetchLevels ({ commit }: ActionContext<LocalStateInterface, StoreInterface>) {
    const options: QueryOptions = {
      query: QUERY_GET_LEVELS
    }

    const { data } = await apolloProvider.defaultClient.query(options)
    commit('SET_LEVELS', data.level)
  },

  async fetchSubjects ({ commit }: ActionContext<LocalStateInterface, StoreInterface>) {
    const options: QueryOptions = {
      query: QUERY_GET_SUBJECTS
    }

    const { data } = await apolloProvider.defaultClient.query(options)
    commit('SET_SUBJECTS', data.subject)
  },

  async fetchLocations ({ commit }: ActionContext<LocalStateInterface, StoreInterface>) {
    const options: QueryOptions = {
      query: QUERY_GET_LOCATIONS
    }

    const { data } = await apolloProvider.defaultClient.query(options)
    commit('SET_LOCATIONS', data.location)
  }
}

export default actions
